import { Box, Button, Divider, Flex, HStack, Icon, Text, VStack, useToast } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate, useSearchParams, useBlocker } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PiCaretLeftBold, PiCaretRightBold, PiFloppyDiskDuotone } from "react-icons/pi";
import { createVotingModel } from "../CreateVotingView/CreateVotingViewModel";
import { TipsSidebar } from "../CreateVotingView/components/TipsSidebar";
import { LiveSummary } from "../CreateVotingView/components/LiveSummary";
import { StepBasics } from "./StepBasics";
import { StepData } from "./StepData";
import { StepSummary } from "./StepSummary";
import { RequirementsChecklist } from "./RequirementsChecklist";
import { GLASS, SCROLL_FOG, EASE } from "../../components/glassUi";

const EASE_ARR = [0.22, 1, 0.36, 1] as const;
const MotionDiv = motion.div;

type V2Step = { key: string; label: string };
// Nowy flow: 3 ekrany — preset → inline kreator (jedno płótno) → podsumowanie (osobny widok).
const S: Record<string, V2Step> = {
  basics: { key: "basics", label: "Punkt startu" },
  build: { key: "build", label: "Ułóż głosowanie" },
  summary: { key: "summary", label: "Podsumowanie" }
};
const buildSteps = (): V2Step[] => [S.basics, S.build, S.summary];

const TIP_KEY: Record<string, string> = { basics: "", build: "data", summary: "rules" };


// Minimalny segmentowy postęp (aktywny wydłuża się) — zamiast steppera.
const Dots: React.FC<{ steps: V2Step[]; cur: number; onStep: (i: number) => void }> = ({ steps, cur, onStep }) => (
  <HStack spacing="7px">
    {steps.map((s, i) => (
      <Box key={s.key} as="button" aria-label={s.label} onClick={() => onStep(i)} h="7px" w={i === cur ? "28px" : "7px"} borderRadius="full" bg={i <= cur ? "primary" : "rgba(24,12,48,0.14)"} transition={`all ${EASE}`} _hover={{ bg: i <= cur ? "#360079" : "rgba(24,12,48,0.28)" }} />
    ))}
  </HStack>
);

const contentVariants = {
  enter: { opacity: 0, y: 16, scale: 0.99 },
  center: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -16, scale: 0.99 }
};

export const CreateVotingViewV2: React.FC = observer(() => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);

  useEffect(() => {
    document.title = "Utwórz głosowanie";
    const grupa = searchParams.get("grupa");
    if (grupa) createVotingModel.preselectGroup(grupa);
    // eslint-disable-next-line
  }, []);

  // Czy formularz ma niezapisaną treść (nie ostrzegaj po publikacji).
  const isDirty = () => {
    if (createVotingModel.confirmData.isVisible) return false;
    const dm = createVotingModel.votingDataModel;
    const ev = createVotingModel.eligibleVotersModel;
    const anyQuestion = Array.from(createVotingModel.votingQuestionsModel.questions.values()).some(q => q.question.trim());
    return !!(dm.name.trim() || dm.description.trim() || anyQuestion || ev.votersEmails.length || ev.selectedGroupIds.length || ev.hasCsv || createVotingModel.votingGoal !== "custom");
  };

  // Ostrzeżenie przy odświeżeniu / zamknięciu karty.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirty()) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
    // eslint-disable-next-line
  }, []);

  // Ostrzeżenie przy opuszczeniu strony w aplikacji (nawigacja SPA).
  const blocker = useBlocker(({ currentLocation, nextLocation }) => isDirty() && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (blocker.state === "blocked") {
      if (window.confirm("Masz niezapisaną treść tworzonego głosowania. Na pewno opuścić stronę? Zmiany przepadną.")) blocker.proceed();
      else blocker.reset();
    }
    // eslint-disable-next-line
  }, [blocker.state]);

  const steps = buildSteps();
  const cur = Math.min(step, steps.length - 1);
  const curKey = steps[cur]?.key ?? "basics";
  const isLast = cur === steps.length - 1;

  const summaryIndex = steps.length - 1;
  const canSummary = createVotingModel.isReadyForSummary;

  // Nawigacja — do „Podsumowania" tylko gdy głosowanie odpowiednio wypełnione.
  const go = (t: number) => {
    const target = Math.max(0, Math.min(steps.length - 1, t));
    if (target === summaryIndex && !createVotingModel.isReadyForSummary) {
      toast({ status: "error", isClosable: true, title: "Uzupełnij zaznaczone pola, aby przejść do podsumowania" });
      return;
    }
    setStep(target);
  };
  const back = () => setStep(Math.max(0, cur - 1));
  const next = () => go(cur + 1);
  const onPublish = () => createVotingModel.handleCreateVoting(toast);

  // Stepper — wspólny węzeł (w karcie każdego kroku).
  const stepper = (
    <Flex align="center" gap="10px">
      <Dots steps={steps} cur={cur} onStep={go} />
      <Text fontSize="11.5px" color="#a8a2b6" fontWeight="700" whiteSpace="nowrap">{cur + 1} / {steps.length}</Text>
    </Flex>
  );

  const stepContent = curKey === "basics" ? (
    <StepBasics model={createVotingModel} stepper={stepper} />
  ) : curKey === "summary" ? (
    <StepSummary model={createVotingModel} stepper={stepper} />
  ) : (
    <StepData model={createVotingModel} stepper={stepper} />
  );

  // Klik w wymaganie → przejdź do kroku „Ułóż głosowanie", przewiń i aktywuj input.
  const gotoReq = (target: string) => {
    go(1);
    setTimeout(() => {
      const el = document.getElementById(target);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusable = el.matches("input, textarea") ? el : el.querySelector("input, textarea, button");
      (focusable as HTMLElement | null)?.focus({ preventScroll: true });
    }, 80);
  };

  const rail = (
    <>
      <RequirementsChecklist model={createVotingModel} onGoto={gotoReq} />
      <Divider my="20px" borderColor="rgba(24,12,48,0.08)" />
      <LiveSummary model={createVotingModel} />
      <Divider my="20px" borderColor="rgba(24,12,48,0.08)" />
      <TipsSidebar stepKey={TIP_KEY[curKey] || "type"} />
    </>
  );

  return (
    <Flex align="flex-start">
      <Box flex="1" minW={0}>
        <Box w="100%" maxW="1000px" mx="auto" px={{ base: "20px", md: "44px" }} py={{ base: "26px", md: "40px" }}>
          {/* Treść — immersyjne przejścia (header/stepper są w kartach kroków) */}
          <Box position="relative">
            <AnimatePresence exitBeforeEnter initial={false}>
              <MotionDiv key={curKey} variants={contentVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.32, ease: EASE_ARR }}>
                {stepContent}
              </MotionDiv>
            </AnimatePresence>
          </Box>

          {/* Rail zwinięty <xl */}
          <Box display={{ base: "block", xl: "none" }} borderRadius="20px" px="20px" py="20px" mt="24px" sx={GLASS}>
            {rail}
          </Box>

          {/* Nawigacja — glass dock (ukryty po publikacji) */}
          {!createVotingModel.confirmData.isVisible && (
          <Box position="sticky" bottom="16px" zIndex={20} mt="28px">
          <Flex align="center" justify="space-between" gap="12px" borderRadius="18px" px={{ base: "14px", md: "16px" }} py="12px" direction={{ base: "column-reverse", sm: "row" }} sx={{ ...GLASS, background: "rgba(255,255,255,0.74)" }} boxShadow="inset 0 1px 0 rgba(255,255,255,0.75), 0 -6px 28px -18px rgba(43,0,90,0.4), 0 18px 44px -26px rgba(43,0,90,0.5)">
            <Button variant="ghost" color="#6b6480" leftIcon={<Icon as={PiCaretLeftBold} boxSize="13px" />} onClick={back} isDisabled={cur === 0} w={{ base: "100%", sm: "auto" }} _hover={{ bg: "rgba(24,12,48,0.05)" }} _active={{ transform: "scale(0.97)" }}>
              Wstecz
            </Button>
            <HStack spacing="12px" w={{ base: "100%", sm: "auto" }}>
              <Button flex={{ base: 1, sm: "none" }} variant="ghost" color="#8a8397" borderRadius="14px" leftIcon={<Icon as={PiFloppyDiskDuotone} />} fontWeight="700" onClick={() => createVotingModel.handleCreateDraft(navigate, toast)} _hover={{ bg: "rgba(24,12,48,0.05)", color: "primary" }} _active={{ transform: "scale(0.97)" }}>
                Zapisz roboczą
              </Button>
              <MotionDiv whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} style={{ flex: "0 0 auto" }}>
                {isLast ? (
                  <Button bg="accent" color="white" borderRadius="14px" fontWeight="800" h="52px" px="34px" fontSize="15px" onClick={onPublish} isLoading={createVotingModel.isPublishing} isDisabled={!canSummary} loadingText="Publikuję…" boxShadow="0 14px 30px -12px rgba(229,31,68,0.6)" _hover={{ bg: "#c41a3a" }} _disabled={{ opacity: 0.5, cursor: "not-allowed", boxShadow: "none" }}>
                    Publikuj
                  </Button>
                ) : (
                  <Button bg="primary" color="white" borderRadius="14px" fontWeight="800" h="52px" px="34px" fontSize="15px" rightIcon={<Icon as={PiCaretRightBold} boxSize="14px" />} onClick={next} isDisabled={cur === summaryIndex - 1 && !canSummary} _disabled={{ opacity: 0.5, cursor: "not-allowed" }} boxShadow="0 14px 30px -12px rgba(67,0,152,0.6)" _hover={{ bg: "#36007a" }}>
                    Dalej
                  </Button>
                )}
              </MotionDiv>
            </HStack>
          </Flex>
          </Box>
          )}
        </Box>
      </Box>

      {/* ── PRAWY SIDEBAR — bez zmian ── */}
      <Box as="aside" display={{ base: "none", xl: "block" }} w="384px" flexShrink={0} alignSelf="stretch">
        <Box position="sticky" top="0" h="100vh" p="12px">
          <Box position="relative" h="100%" borderRadius="26px" overflow="hidden" sx={GLASS}>
            <Box position="absolute" inset={0} pointerEvents="none" sx={{ background: "radial-gradient(58% 40% at 50% 20%, rgba(124,58,237,0.14), transparent 72%)" }} />
            <VStack
              position="relative"
              align="stretch"
              spacing="0"
              h="100%"
              px="20px"
              py="24px"
              sx={{
                ...SCROLL_FOG,
                maskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)",
                WebkitMaskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)"
              }}
            >
              {rail}
            </VStack>
          </Box>
        </Box>
      </Box>
    </Flex>
  );
});
